"use client";

import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { 
  MessageSquare, 
  Star, 
  CheckCircle, 
  ArrowLeft, 
  ChevronLeft,
  ChevronRight,
  Send,
  AlertCircle,
} from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { api } from "@/lib/api";
import { toast } from "react-hot-toast";
import { StandaloneThemeToggle } from "@/components/StandaloneThemeToggle";
import { cn } from "@/lib/utils";

interface FeedbackQuestion {
  id: string;
  text: string;
  questionType: 'text' | 'rating' | 'multiple_choice';
  isRequired: boolean;
  sortOrder: number;
  options?: any; // JSON field from database
}

interface FeedbackCycle {
  id: string;
  name: string;
  description: string;
  status: string;
  startDate: string;
  endDate: string;
  targetUserId?: string;
  targetUser?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    avatar?: string;
    name?: string;
  };
  questions: FeedbackQuestion[];
  isAnonymous?: boolean;
  cycleType: 'regular' | 'recurring' | 'targeted';
  schedule?: {
    dueInDays?: number;
    currentRunDate?: string | null;
    nextRunDate?: string | null;
    startDate?: string | null;
  } | null;
  dueDate?: string | null;
}

interface FeedbackFormProps {
  cycle: FeedbackCycle;
  encodedUserInfo?: { userId?: string; participantId?: string; token?: string | null } | null;
  participantContext?: {
    id: string;
    invitedAt?: string;
    email?: string | null;
    user?: {
      name?: string | null;
      email?: string | null;
    } | null;
  } | null;
}

interface FormResponse {
  questionId: string;
  responseText?: string;
  responseRating?: number;
  responseChoice?: string;
}

export function FeedbackForm({ cycle, encodedUserInfo, participantContext }: FeedbackFormProps) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAuthenticated = Boolean(user?.id);
  
  // Get participant identification from URL
  const [participantToken, setParticipantToken] = useState<string | null>(null);
  const [participantEmail, setParticipantEmail] = useState<string | null>(null);
  
  useEffect(() => {
    if (encodedUserInfo?.token) {
      setParticipantToken(encodedUserInfo.token);
      if (participantContext?.user?.email || participantContext?.email) {
        setParticipantEmail(participantContext?.user?.email ?? participantContext?.email ?? null);
      }
      return;
    }

    // Priority 1: Use encoded user info from URL (if available)
    if (encodedUserInfo?.userId) {
      return;
    }
    
    // Priority 2: Get URL parameters for participant identification
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const email = urlParams.get('email');
    
    // If token is provided in URL, save it to localStorage for future visits
    if (token) {
      localStorage.setItem(`feedback_token_${cycle.id}`, token);
      setParticipantToken(token);
    } else {
      // Try to get token from localStorage if not in URL
      const savedToken = localStorage.getItem(`feedback_token_${cycle.id}`);
      setParticipantToken(savedToken);
    }
    
    if (email) {
      localStorage.setItem(`feedback_email_${cycle.id}`, email);
      setParticipantEmail(email);
    } else {
      // Try to get email from localStorage if not in URL
      const savedEmail = localStorage.getItem(`feedback_email_${cycle.id}`);
      setParticipantEmail(savedEmail);
    }
    
    // Participant identification fallback stored in localStorage for repeat visits
  }, [cycle.id, encodedUserInfo, participantContext]);
  const [responses, setResponses] = useState<Record<string, FormResponse>>({});
  const [overallRating, setOverallRating] = useState<number | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const [existingResponses, setExistingResponses] = useState<any[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoadingExisting, setIsLoadingExisting] = useState(true);
  const [viewMode, setViewMode] = useState<'paginated' | 'all'>('paginated');

  // Sort questions by sortOrder, or use sample questions for testing if none exist
  const judgments = cycle?.questions ?? [];
  const sortedQuestions = useMemo(() => {
    if (!judgments.length) return [];
    return [...judgments].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [judgments]);

  const dueDateValue = useMemo(() => {
    const candidate =
      cycle?.dueDate ||
      cycle?.schedule?.currentRunDate ||
      cycle?.schedule?.nextRunDate ||
      cycle?.endDate ||
      cycle?.startDate;
    if (!candidate) return null;
    const parsed = new Date(candidate);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString();
  }, [cycle]);

  const targetDisplayName = useMemo(() => {
    const target = cycle?.targetUser;
    if (!target) return null;
    return (
      target.name ||
      [target.firstName, target.lastName].filter(Boolean).join(' ') ||
      target.email ||
      null
    );
  }, [cycle?.targetUser]);

  // Fetch existing responses if user is identified
  useEffect(() => {
    const fetchExistingResponses = async () => {
      if (encodedUserInfo?.token) {
        setIsLoadingExisting(false);
        return;
      }

      const userId = user?.id;
      const encodedUserId = encodedUserInfo?.userId;
      const hasIdentification = encodedUserId || participantToken || participantEmail || userId;
      
      if (!hasIdentification) {
        setIsLoadingExisting(false);
        return;
      }

      try {
        const params: any = {};
        if (encodedUserId) {
          params.userId = encodedUserId;
        } else if (userId) {
          params.respondentId = userId;
        } else if (participantToken) {
          params.token = participantToken;
        } else if (participantEmail) {
          params.email = participantEmail;
        }
        
        const result = await api.get<any>(
          `/feedback-cycles/${cycle.id}/responses`,
          Object.keys(params).length ? { params } : undefined
        );
        if (result.success && result.data) {
          setExistingResponses(result.data);
          setIsEditing(result.data.length > 0);
        }
      } catch (error) {
        console.error('Failed to fetch existing responses:', error);
      } finally {
        setIsLoadingExisting(false);
      }
    };

    fetchExistingResponses();
  }, [cycle.id, user?.id, participantToken, participantEmail, encodedUserInfo]);

  useEffect(() => {
    // Initialize responses object
    const initialResponses: Record<string, FormResponse> = {};
    
    sortedQuestions.forEach(question => {
      // Check if there's an existing response for this question
      const existingResponse = existingResponses.find(r => r.questionId === question.id);
      
      initialResponses[question.id] = {
        questionId: question.id,
        responseText: existingResponse?.responseText || undefined,
        responseRating: existingResponse?.responseRating || undefined,
        responseChoice: existingResponse?.responseChoice || undefined,
      };
    });
    
    setResponses(initialResponses);
  }, [sortedQuestions, existingResponses]);

  const handleResponseChange = (questionId: string, field: keyof FormResponse, value: any) => {
    setResponses(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value,
      }
    }));
  };

  const validateResponse = (question: FeedbackQuestion, response: FormResponse): boolean => {
    if (!question.isRequired) return true;
    if (!response) return false;
    
    switch (question.questionType) {
      case 'text':
        return !!response.responseText?.trim();
      case 'rating':
        return typeof response.responseRating === 'number';
      case 'multiple_choice':
        return !!response.responseChoice;
      default:
        return true;
    }
  };

  const isFormValid = (): boolean => {
    const allQuestionsValid = sortedQuestions.every(question => 
      validateResponse(question, responses[question.id])
    );
    const overallRatingValid = overallRating !== null && overallRating >= 1 && overallRating <= 10;
    return allQuestionsValid && overallRatingValid;
  };

  const getCompletedCount = (): number => {
    return sortedQuestions.filter(question => 
      validateResponse(question, responses[question.id])
    ).length;
  };

  const navigateToQuestion = (index: number) => {
    if (index >= 0 && index < sortedQuestions.length) {
      setCurrentQuestionIndex(index);
    }
  };

  const goToNextQuestion = () => {
    if (currentQuestionIndex < sortedQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const goToPreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleSubmit = async () => {
    if (!isFormValid()) {
      toast.error('Please complete all required fields');
      return;
    }

    setIsSubmitting(true);
    const loadingToast = toast.loading(isEditing ? 'Updating feedback...' : 'Submitting feedback...');
    
    try {
      const responseData = {
        responses: Object.values(responses)
          .filter(response => {
            const question = sortedQuestions.find(q => q.id === response.questionId);
            return question && validateResponse(question, response);
          })
          .map(response => ({
            questionId: response.questionId,
            ...(response.responseText && { responseText: response.responseText }),
            ...(response.responseRating && { responseRating: response.responseRating }),
            ...(response.responseChoice && { responseChoice: response.responseChoice }),
          })),
        ...(overallRating && { overallRating }),
      };

      // Pass participant identification to API
      let endpoint = `/feedback-cycles/${cycle.id}/responses`;
      let options: any = undefined;

      if (encodedUserInfo?.token) {
        endpoint = `/feedback/${encodedUserInfo.token}`;
      } else {
        const submitParams: any = {};
        if (encodedUserInfo?.userId) {
          submitParams.userId = encodedUserInfo.userId;
        } else if (participantToken) {
          submitParams.token = participantToken;
        } else if (participantEmail) {
          submitParams.email = participantEmail;
        }

        if (Object.keys(submitParams).length > 0) {
          options = { params: submitParams };
        }
      }

      await api.post(endpoint, responseData, options);
      
      setShowSuccess(true);
      toast.success(isEditing ? 'Feedback updated successfully' : 'Feedback submitted successfully', { id: loadingToast });
      
      if (isAuthenticated) {
        // Redirect authenticated users back into the app context
        setTimeout(() => {
          navigate('/feedback');
        }, 3000);
      }
    } catch (error: any) {
      console.error('Error submitting feedback:', error);
      toast.error(error.message || (isEditing ? 'Failed to update feedback' : 'Failed to submit feedback'), { id: loadingToast });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-full max-w-lg mx-6">
          <div className="surface-elevated p-16 shadow-medium text-center">
            <div className="w-16 h-16 bg-emerald-50 dark:bg-emerald-900/20 rounded-full flex items-center justify-center mx-auto mb-12">
              <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-2xl font-light text-foreground mb-6">
              {isEditing ? 'Updated' : 'Submitted'}
            </h2>
            <p className="text-muted-foreground mb-12 font-light">
              {isEditing 
                ? 'Your feedback has been updated'
                : 'Thank you for your feedback'
              }
            </p>
            {isAuthenticated && (
              <Link 
                to="/feedback" 
                className="inline-flex items-center gap-3 px-8 py-3 bg-foreground text-background rounded-full font-medium hover:scale-105 transition-all duration-300"
              >
                Return
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Show loading state while fetching existing responses
  if (isLoadingExisting) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-6">
          <div className="animate-spin rounded-full h-8 w-8 border border-border border-t-foreground mx-auto"></div>
          <p className="text-muted-foreground font-light">Loading</p>
        </div>
      </div>
    );
  }

  // Handle case when there are no questions
  if (sortedQuestions.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <Card className="p-8 text-center">
            <div className="mb-4">
              <MessageSquare className="w-16 h-16 mx-auto text-gray-600 dark:text-gray-300 mb-4" />
              <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">No Questions Available</h2>
              <p className="text-gray-600 dark:text-gray-300">
                This feedback cycle doesn't have any questions configured yet.
                Please contact the cycle administrator to add questions.
              </p>
            </div>
            <Link to="/feedback">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </Card>
        </div>
      </div>
    );
  }

  const currentQuestion = sortedQuestions[currentQuestionIndex];
  const progress = ((getCompletedCount() / sortedQuestions.length) * 100);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <header className="flex items-center justify-between mb-6">
          <Link to="/feedback" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Back</span>
          </Link>
          <StandaloneThemeToggle />
        </header>
          
        {/* Title */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-semibold mb-2">
            {cycle.name}
          </h1>
          {cycle.description && (
            <p className="text-muted-foreground mb-4">
              {cycle.description}
            </p>
          )}
          <div className="text-sm text-muted-foreground">
            {dueDateValue ? `Due ${dueDateValue}` : 'Due date to be announced'}
            {targetDisplayName && ` • For ${targetDisplayName}`}
            {isEditing && ` • Editing`}
          </div>
        </div>

        {/* Progress */}
        <div className="mb-6">
          <div className="flex justify-between text-sm mb-2">
            <span>Progress</span>
            <span>{getCompletedCount()}/{sortedQuestions.length}</span>
          </div>
          <div className="h-2 bg-muted rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all duration-500 ease-out relative"
              style={{ 
                width: `${progress}%`,
                background: progress < 33 
                  ? 'linear-gradient(90deg, #ef4444 0%, #f59e0b 100%)' 
                  : progress < 66 
                  ? 'linear-gradient(90deg, #f59e0b 0%, #eab308 100%)'
                  : progress < 100
                  ? 'linear-gradient(90deg, #eab308 0%, #22c55e 100%)'
                  : 'linear-gradient(90deg, #22c55e 0%, #10b981 100%)'
              }}
            >
              {/* Shimmer effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-pulse" />
            </div>
          </div>
        </div>

        {/* View Toggle */}
        {sortedQuestions.length > 1 && (
          <div className="flex justify-center mb-6">
            <div className="inline-flex bg-muted rounded-lg p-1 border border-border">
              <button
                onClick={() => setViewMode('paginated')}
                className={cn(
                  "px-4 py-2 text-sm rounded-md font-medium transition-all duration-200",
                  viewMode === 'paginated' 
                    ? 'bg-background text-foreground shadow-sm border border-border' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                )}
              >
                One at a Time
              </button>
              <button
                onClick={() => setViewMode('all')}
                className={cn(
                  "px-4 py-2 text-sm rounded-md font-medium transition-all duration-200",
                  viewMode === 'all' 
                    ? 'bg-background text-foreground shadow-sm border border-border' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                )}
              >
                Show All
              </button>
            </div>
          </div>
        )}

        {/* Questions */}
        {viewMode === 'paginated' ? (
          <div className="bg-card rounded-lg border border-border p-6">
            {/* Question Navigation */}
            {sortedQuestions.length > 1 && (
              <div className="flex justify-center mb-6">
                <div className="flex gap-1">
                  {sortedQuestions.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => navigateToQuestion(index)}
                      className={cn(
                        "w-8 h-8 rounded text-sm font-medium",
                        index === currentQuestionIndex
                          ? 'bg-primary text-primary-foreground'
                          : validateResponse(sortedQuestions[index], responses[sortedQuestions[index].id])
                          ? 'bg-emerald-600 text-white'
                          : 'bg-muted hover:bg-muted/80'
                      )}
                    >
                      {index + 1}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Question */}
            {sortedQuestions.length > 0 && (
              <>
                <div className="text-center mb-6">
                  <div className="text-lg font-medium mb-2">
                    {currentQuestion.text}
                    {currentQuestion.isRequired && <span className="text-destructive ml-1">*</span>}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Question {currentQuestionIndex + 1} of {sortedQuestions.length}
                  </div>
                </div>

                <div className="max-w-2xl mx-auto">
                {currentQuestion.questionType === 'text' && (
                  <textarea
                    placeholder="Your response..."
                    value={responses[currentQuestion.id]?.responseText || ''}
                    onChange={(e) => handleResponseChange(currentQuestion.id, 'responseText', e.target.value)}
                    className="w-full h-32 p-4 border border-border rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    rows={6}
                  />
                )}
                  
                {currentQuestion.questionType === 'rating' && (
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm text-muted-foreground">
                      <span>Poor</span>
                      <span>Excellent</span>
                    </div>
                    <div className="flex gap-2 justify-center">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => handleResponseChange(currentQuestion.id, 'responseRating', rating)}
                          className={cn(
                            "w-10 h-10 rounded-lg font-medium border",
                            responses[currentQuestion.id]?.responseRating === rating
                              ? 'bg-primary text-primary-foreground border-primary'
                              : 'bg-background hover:bg-muted border-border'
                          )}
                        >
                          {rating}
                        </button>
                      ))}
                    </div>
                    {responses[currentQuestion.id]?.responseRating && (
                      <div className="text-center text-sm text-muted-foreground">
                        Rating: {responses[currentQuestion.id]?.responseRating}/10
                      </div>
                    )}
                  </div>
                )}
                  
                  {currentQuestion.questionType === 'multiple_choice' && (
                    <div className="max-w-md mx-auto">
                      {!currentQuestion.options && (
                        <div className="p-6 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-900/20 rounded-xl mb-8">
                          <p className="text-amber-700 dark:text-amber-400 text-sm flex items-center gap-2 justify-center">
                            <AlertCircle className="w-4 h-4" />
                            No options configured
                          </p>
                        </div>
                      )}
                      {currentQuestion.options && (
                        <div className="space-y-2">
                          {(() => {
                            try {
                              const options = Array.isArray(currentQuestion.options) 
                                ? currentQuestion.options 
                                : JSON.parse(currentQuestion.options || '[]');
                              
                              if (!Array.isArray(options) || options.length === 0) {
                                return (
                                  <div className="p-6 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-900/20 rounded-xl">
                                    <p className="text-amber-700 dark:text-amber-400 text-sm flex items-center gap-2 justify-center">
                                      <AlertCircle className="w-4 h-4" />
                                      Invalid options
                                    </p>
                                  </div>
                                );
                              }
                              
                              return options.map((option: string, optionIndex: number) => (
                                <label 
                                  key={optionIndex} 
                                  className={cn(
                                    "flex items-center gap-4 p-4 rounded-xl cursor-pointer transition-all duration-300 border",
                                    responses[currentQuestion.id]?.responseChoice === option
                                      ? "border-foreground bg-muted"
                                      : "border-border hover:border-border/80 hover:bg-muted/50"
                                  )}
                                  onClick={() => handleResponseChange(currentQuestion.id, 'responseChoice', option)}
                                >
                                  <input
                                    type="radio"
                                    name={`question-${currentQuestion.id}`}
                                    value={option}
                                    checked={responses[currentQuestion.id]?.responseChoice === option}
                                    onChange={() => handleResponseChange(currentQuestion.id, 'responseChoice', option)}
                                    className="sr-only"
                                  />
                                  <div className={cn(
                                    "w-4 h-4 rounded-full border-2 transition-all duration-300 flex items-center justify-center",
                                    responses[currentQuestion.id]?.responseChoice === option
                                      ? "border-foreground"
                                      : "border-border"
                                  )}>
                                    {responses[currentQuestion.id]?.responseChoice === option && (
                                      <div className="w-2 h-2 bg-foreground rounded-full"></div>
                                    )}
                                  </div>
                                  <span className="text-foreground flex-1 font-light">
                                    {option}
                                  </span>
                                </label>
                              ));
                            } catch (error) {
                              console.error('Error parsing multiple choice options:', error, currentQuestion);
                              return (
                                <div className="p-6 bg-destructive/5 border border-destructive/15 rounded-xl">
                                  <p className="text-destructive text-sm flex items-center gap-2 justify-center">
                                    <AlertCircle className="w-4 h-4" />
                                    Error parsing options
                                  </p>
                                </div>
                              );
                            }
                          })()}
                        </div>
                      )}
                    </div>
                  )}

                  {!['text', 'rating', 'multiple_choice'].includes(currentQuestion.questionType) && (
                    <div className="max-w-lg mx-auto">
                      <div className="p-6 bg-muted/30 border border-border rounded-xl mb-6">
                        <p className="text-black dark:text-white text-sm mb-2 flex items-center gap-2 justify-center">
                          <AlertCircle className="w-4 h-4" />
                          Unknown type: <code className="bg-muted px-2 py-1 rounded text-xs">{currentQuestion.questionType}</code>
                        </p>
                        <p className="text-muted-foreground text-xs text-center">
                          Using text fallback
                        </p>
                      </div>
                      <textarea
                        placeholder="Your response..."
                        value={responses[currentQuestion.id]?.responseText || ''}
                        onChange={(e) => handleResponseChange(currentQuestion.id, 'responseText', e.target.value)}
                        className="w-full min-h-[160px] p-6 border-0 border-b border-border bg-transparent text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:border-foreground transition-all duration-300 text-center font-light leading-relaxed"
                        rows={6}
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Navigation */}
            {sortedQuestions.length > 1 && (
              <div className="flex justify-between mt-6 pt-6 border-t border-border">
                <button
                  onClick={goToPreviousQuestion}
                  disabled={currentQuestionIndex === 0}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg",
                    currentQuestionIndex === 0
                      ? "text-muted-foreground cursor-not-allowed"
                      : "text-foreground hover:bg-muted"
                  )}
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                
                <button
                  onClick={goToNextQuestion}
                  disabled={currentQuestionIndex === sortedQuestions.length - 1}
                  className={cn(
                    "flex items-center gap-2 px-4 py-2 rounded-lg",
                    currentQuestionIndex === sortedQuestions.length - 1
                      ? "text-muted-foreground cursor-not-allowed"
                      : "text-foreground hover:bg-muted"
                  )}
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        ) : (
          /* Show All View - Simplified */
          <div className="space-y-8 max-w-2xl mx-auto">
            {sortedQuestions.map((question, index) => (
              <div key={question.id} className="border border-border rounded-xl p-8">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-6 h-6 bg-foreground text-background rounded-full flex items-center justify-center text-xs font-bold">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-light text-foreground">
                    {question.text}
                  </h3>
                  {question.isRequired && (
                    <div className="w-1.5 h-1.5 bg-destructive rounded-full"></div>
                  )}
                </div>
                
                {question.questionType === 'text' && (
                  <textarea
                    placeholder="Your thoughts..."
                    value={responses[question.id]?.responseText || ''}
                    onChange={(e) => handleResponseChange(question.id, 'responseText', e.target.value)}
                    className="w-full min-h-[120px] p-4 border border-border rounded-lg bg-background text-foreground placeholder:text-muted-foreground resize-none focus:outline-none focus:border-foreground transition-all duration-300"
                  />
                )}
                
                {question.questionType === 'rating' && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Poor</span>
                      <span>Excellent</span>
                    </div>
                    <div className="flex gap-1 justify-center">
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                        <button
                          key={rating}
                          type="button"
                          onClick={() => handleResponseChange(question.id, 'responseRating', rating)}
                          className={cn(
                            "w-8 h-8 rounded-full font-medium transition-all duration-300",
                            responses[question.id]?.responseRating === rating
                              ? 'bg-foreground text-background'
                              : 'bg-muted text-muted-foreground hover:bg-muted/80'
                          )}
                        >
                          <span className="text-xs">{rating}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {question.questionType === 'multiple_choice' && question.options && (
                  <div className="space-y-2">
                    {(() => {
                      try {
                        const options = Array.isArray(question.options) 
                          ? question.options 
                          : JSON.parse(question.options || '[]');
                        
                        return options.map((option: string, optionIndex: number) => (
                          <label 
                            key={optionIndex} 
                            className={cn(
                              "flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-all duration-300 border",
                              responses[question.id]?.responseChoice === option
                                ? "border-foreground bg-muted"
                                : "border-border hover:bg-muted"
                            )}
                            onClick={() => handleResponseChange(question.id, 'responseChoice', option)}
                          >
                            <input
                              type="radio"
                              name={`question-${question.id}`}
                              value={option}
                              checked={responses[question.id]?.responseChoice === option}
                              onChange={() => handleResponseChange(question.id, 'responseChoice', option)}
                              className="sr-only"
                            />
                            <div className={cn(
                              "w-3 h-3 rounded-full border transition-all duration-300",
                              responses[question.id]?.responseChoice === option
                                ? "border-foreground bg-foreground"
                                : "border-border"
                            )}></div>
                            <span className="text-sm text-foreground">
                              {option}
                            </span>
                          </label>
                        ));
                      } catch (error) {
                        return <p className="text-destructive text-sm">Error loading options</p>;
                      }
                    })()}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Overall Rating Section */}
        <div className="mt-12 pt-8 border-t border-border">
          <div className="bg-card rounded-xl border border-border p-8 shadow-sm">
            <div className="text-center space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-foreground mb-3">Overall Rating</h3>
                <p className="text-muted-foreground mb-6">
                  How would you rate your overall experience? (1-10 scale)
                </p>
              </div>
              
              <div className="flex justify-center items-center gap-2 flex-wrap">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((rating) => (
                  <button
                    key={rating}
                    type="button"
                    onClick={() => setOverallRating(rating)}
                    className={cn(
                      "w-10 h-10 rounded-lg font-semibold text-sm transition-all duration-300 border-2",
                      overallRating === rating
                        ? 'bg-primary text-primary-foreground border-primary shadow-lg'
                        : 'bg-background hover:bg-muted border-border hover:border-border/80'
                    )}
                  >
                    {rating}
                  </button>
                ))}
              </div>
              
              {overallRating && (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 bg-muted px-4 py-2 rounded-lg">
                    <Star className="h-4 w-4 fill-primary text-primary" />
                    <span className="text-sm font-medium">
                      {overallRating <= 2 && "Poor"}
                      {overallRating >= 3 && overallRating <= 4 && "Below Average"} 
                      {overallRating >= 5 && overallRating <= 6 && "Average"}
                      {overallRating >= 7 && overallRating <= 8 && "Good"}
                      {overallRating >= 9 && overallRating <= 10 && "Excellent"}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Enhanced Submit Section */}
        <div className="mt-8 relative">
          <div className="bg-card rounded-xl border border-border p-8 shadow-lg">
            <div className="text-center space-y-6">
              <div className="relative">
                <h3 className="text-2xl font-semibold text-foreground mb-3">
                  {isEditing ? 'Update Your Responses' : 'Submit Your Feedback'}
                </h3>
                <p className="text-base text-muted-foreground">
                  {isEditing 
                    ? 'Review and save your changes below'
                    : 'Thank you for taking the time to provide your feedback'
                  }
                </p>
              </div>
              
              <div className="flex items-center justify-center gap-6 py-4">
                <div className="flex items-center gap-3 px-4 py-2 bg-muted/50 rounded-lg border border-border">
                  <CheckCircle className="w-5 h-5 text-emerald-500" />
                  <span className="text-sm text-muted-foreground font-medium">
                    {getCompletedCount()}/{sortedQuestions.length} Questions Complete
                  </span>
                </div>
                {!isFormValid() && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-destructive/5 text-destructive rounded-lg border border-destructive/15 backdrop-blur-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Some required fields are incomplete</span>
                  </div>
                )}
              </div>
              
              <div className="relative">
                <button
                  onClick={handleSubmit}
                  disabled={!isFormValid() || isSubmitting}
                  className={cn(
                    "px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 border w-full sm:w-auto",
                    isFormValid() && !isSubmitting
                      ? "bg-foreground text-background border-foreground hover:scale-105 hover:shadow-lg hover:shadow-foreground/20"
                      : "bg-muted text-muted-foreground border-border cursor-not-allowed"
                  )}
                >
                  <span className="flex items-center justify-center gap-3">
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent"></div>
                        <span>{isEditing ? 'Updating...' : 'Submitting...'}</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>{isEditing ? 'Update Feedback' : 'Submit Feedback'}</span>
                      </>
                    )}
                  </span>
                </button>
              </div>
              
              {cycle.isAnonymous && (
                <div className="flex items-center justify-center gap-3 pt-4">
                  <div className="w-2 h-2 bg-muted-foreground/40 rounded-full"></div>
                  <p className="text-sm text-muted-foreground font-medium">
                    This submission is anonymous
                  </p>
                  <div className="w-2 h-2 bg-muted-foreground/40 rounded-full"></div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
